import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, CheckCircle, PlayCircle, Settings, Users, Truck, Package,
  Calendar, MessageSquare, Clock, Globe, AlertCircle, HelpCircle, Mail, Smartphone,
  MapPin, FileText, Zap,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import SEO from '../../components/SEO';

/* ----- Small reusable styled "browser" mockup frame ----- */
const MockFrame = ({ url, children }) => (
  <div className="mt-6 rounded-xl overflow-hidden border border-gray-200 shadow-md bg-white">
    <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-4 py-2.5 flex items-center gap-3">
      <div className="flex gap-1.5">
        <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
      </div>
      <div className="flex-1 bg-slate-600/60 rounded px-3 py-1 text-[11px] text-slate-200 font-mono truncate">
        {url}
      </div>
    </div>
    <div className="bg-slate-50">{children}</div>
  </div>
);

const FakeField = ({ label, value, placeholder }) => (
  <div>
    <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{label}</label>
    <div className={`mt-1 border border-gray-200 rounded-md px-3 py-2 text-sm ${value ? 'text-gray-800 bg-white' : 'text-gray-400 bg-gray-50 italic'}`}>
      {value || placeholder}
    </div>
  </div>
);

const QuickStartGuidePage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Quick Start Guide - MoveMate Documentation"
        description="Get started with MoveMate in under 10 minutes. Learn how to set up your moving company, configure pricing, invite your team, and create your first job."
        url="https://movemate.tech/docs/quick-start"
      />

      {/* Header */}
      <header className="bg-gradient-to-r from-primary to-cyan-500 text-white py-10 px-4" data-testid="quickstart-header">
        <div className="max-w-4xl mx-auto">
          <Link to="/docs" className="inline-flex items-center text-white/80 hover:text-white mb-4 transition" data-testid="back-to-docs-link">
            <ArrowLeft size={20} className="mr-2" />
            Back to Documentation
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <PlayCircle size={32} />
            <span className="text-sm bg-white/20 px-3 py-1 rounded-full">Getting Started</span>
            <span className="text-sm bg-white/10 px-3 py-1 rounded-full flex items-center gap-1.5"><Clock size={14} /> 10 min read</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold">Quick Start Guide</h1>
          <p className="text-cyan-100 mt-3 max-w-2xl">
            Go from empty dashboard to your first live job in under 10 minutes. This guide covers the full setup — no prior experience required.
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto py-12 px-4">
        {/* Before you start */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-8 border-l-4 border-primary" data-testid="prerequisites-card">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <AlertCircle className="text-primary" size={22} /> Before You Start
          </h2>
          <p className="text-gray-600 mb-5">Have these handy to finish setup in one sitting:</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <Mail className="text-primary flex-shrink-0 mt-0.5" size={18} />
              <div>
                <p className="font-medium text-gray-900">Business email</p>
                <p className="text-sm text-gray-500">Used for your admin login and customer emails</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <FileText className="text-primary flex-shrink-0 mt-0.5" size={18} />
              <div>
                <p className="font-medium text-gray-900">Company logo (PNG/SVG)</p>
                <p className="text-sm text-gray-500">Shows on estimates, invoices, and the customer portal</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Users className="text-primary flex-shrink-0 mt-0.5" size={18} />
              <div>
                <p className="font-medium text-gray-900">Team member emails</p>
                <p className="text-sm text-gray-500">Drivers, porters, and office staff you want to invite</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="text-primary flex-shrink-0 mt-0.5" size={18} />
              <div>
                <p className="font-medium text-gray-900">Your pricing basics</p>
                <p className="text-sm text-gray-500">Hourly rates, per-mile charge, VAT %, and any materials you sell</p>
              </div>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Welcome to MoveMate!</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            MoveMate is your all-in-one platform for managing moving jobs, inventory, customers, and teams.
            This guide walks you through the six essential steps to go from a blank dashboard to a ready-to-run operation.
          </p>
          <div className="grid sm:grid-cols-3 gap-3 mt-5">
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-700">6</div>
              <div className="text-xs text-blue-700 uppercase tracking-wide mt-1">Setup steps</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-700">~10 min</div>
              <div className="text-xs text-green-700 uppercase tracking-wide mt-1">Total time</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-700">14-day</div>
              <div className="text-xs text-purple-700 uppercase tracking-wide mt-1">Free trial</div>
            </div>
          </div>
        </div>

        {/* Step 1 — Register */}
        <StepCard
          number={1}
          title="Create Your Company Account"
          subtitle="Set up your moving company profile"
          testid="step-1"
        >
          <p>Start by registering your company:</p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>Go to <Link to="/register-company" className="text-primary hover:underline font-medium">Register Company</Link></li>
            <li>Enter your company name, email, and create a password</li>
            <li>Verify your email address by clicking the link we send you</li>
            <li>Log in to access your dashboard</li>
          </ol>
          <TipBox tone="blue">
            Use your business email for better credibility with customers who receive automated estimates and invoices.
          </TipBox>

          <MockFrame url="https://admin.movemate.tech/register-company">
            <div className="p-6 max-w-md mx-auto">
              <div className="text-center mb-5">
                <div className="inline-block w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-cyan-500 mb-2" />
                <h4 className="font-bold text-gray-900">Register Your Company</h4>
                <p className="text-xs text-gray-500">Start your 14-day free trial</p>
              </div>
              <div className="space-y-3">
                <FakeField label="Company Name" value="Acme Moving Ltd" />
                <FakeField label="Business Email" value="hello@acmemoving.co.uk" />
                <FakeField label="Password" placeholder="••••••••" />
                <button className="w-full bg-gradient-to-r from-primary to-cyan-500 text-white rounded-md py-2.5 text-sm font-semibold mt-1">
                  Create Company Account
                </button>
              </div>
            </div>
          </MockFrame>
        </StepCard>

        {/* Step 2 — Company Settings */}
        <StepCard
          number={2}
          title="Configure Company Settings"
          subtitle="Customize MoveMate for your business"
          testid="step-2"
        >
          <p>Navigate to <strong>Settings → Company Settings</strong> to configure:</p>
          <ul className="space-y-3 ml-1">
            <BulletCheck>
              <strong>Company Details:</strong> logo, address, phone, and business hours
            </BulletCheck>
            <BulletCheck>
              <strong>Service Area:</strong> regions you serve (used for travel-time estimates)
            </BulletCheck>
            <BulletCheck>
              <strong>Currency:</strong> GBP, USD, EUR — affects every quote and invoice
            </BulletCheck>
            <BulletCheck>
              <strong>Branding:</strong> primary colour, email signature, and portal appearance
            </BulletCheck>
          </ul>

          <MockFrame url="https://admin.movemate.tech/settings/company">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary to-cyan-500 flex items-center justify-center text-white font-bold">AM</div>
                <div>
                  <div className="font-semibold text-gray-900">Acme Moving Ltd</div>
                  <div className="text-xs text-gray-500">hello@acmemoving.co.uk</div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <FakeField label="Phone" value="+44 20 7946 0958" />
                <FakeField label="Currency" value="GBP (£)" />
                <FakeField label="VAT Number" value="GB 123 4567 89" />
                <FakeField label="Address" value="12 High Street, London" />
              </div>
              <div className="flex justify-end mt-4">
                <div className="bg-primary text-white px-4 py-1.5 rounded text-xs font-semibold">Save Changes</div>
              </div>
            </div>
          </MockFrame>
        </StepCard>

        {/* Step 3 — Pricing */}
        <StepCard
          number={3}
          title="Set Up Pricing"
          subtitle="Configure rates and charges"
          testid="step-3"
        >
          <p>Go to <strong>Settings → Estimate Settings</strong> to configure your pricing:</p>
          <ul className="space-y-3 ml-1">
            <BulletIcon icon={Settings} color="text-primary">
              <strong>General:</strong> VAT rate, deposit percentage, quote validity
            </BulletIcon>
            <BulletIcon icon={Truck} color="text-primary">
              <strong>Distance:</strong> per-mile/km rates for travel charges
            </BulletIcon>
            <BulletIcon icon={Users} color="text-primary">
              <strong>Labour:</strong> hourly rates for porters, drivers, and supervisors
            </BulletIcon>
            <BulletIcon icon={Package} color="text-primary">
              <strong>Materials:</strong> prices for boxes, tape, and packing supplies
            </BulletIcon>
          </ul>

          <MockFrame url="https://admin.movemate.tech/settings/estimate">
            <div className="p-6">
              <div className="flex gap-4 text-xs mb-4 border-b border-gray-200">
                {['General', 'Distance', 'Labour', 'Materials'].map((t, i) => (
                  <div key={t} className={`pb-2 ${i === 2 ? 'border-b-2 border-primary text-primary font-semibold' : 'text-gray-500'}`}>{t}</div>
                ))}
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <FakeField label="Driver / hour" value="£28.00" />
                <FakeField label="Porter / hour" value="£22.00" />
                <FakeField label="Supervisor / hour" value="£35.00" />
                <FakeField label="Overtime multiplier" value="1.5x" />
                <FakeField label="Minimum charge" value="2 hours" />
                <FakeField label="Travel time" value="Billable" />
              </div>
            </div>
          </MockFrame>
        </StepCard>

        {/* Step 4 — Team */}
        <StepCard
          number={4}
          title="Add Team Members"
          subtitle="Invite your staff to the platform"
          testid="step-4"
        >
          <p>Go to <strong>Team → User Management</strong> to add your staff:</p>
          <ul className="space-y-3 ml-1">
            <BulletIcon icon={Users} color="text-indigo-500">
              <strong>Admin:</strong> full access to all features and settings
            </BulletIcon>
            <BulletIcon icon={Truck} color="text-blue-500">
              <strong>Driver:</strong> GPS tracking, job assignments, delivery confirmation
            </BulletIcon>
            <BulletIcon icon={Package} color="text-green-500">
              <strong>Porter:</strong> inventory scanning, loading/unloading
            </BulletIcon>
            <BulletIcon icon={MessageSquare} color="text-orange-500">
              <strong>Sales Rep:</strong> customer communication, estimates
            </BulletIcon>
          </ul>
          <TipBox tone="amber">
            <strong>Note:</strong> Team members receive an email invitation to set up their account and download the mobile app (iOS &amp; Android).
          </TipBox>

          <MockFrame url="https://admin.movemate.tech/team/users">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-900">Team Members</h4>
                <div className="bg-primary text-white px-3 py-1.5 rounded text-xs font-semibold">+ Invite User</div>
              </div>
              <div className="divide-y divide-gray-100">
                {[
                  { name: 'Sarah Okafor', role: 'Admin', color: 'bg-indigo-100 text-indigo-700' },
                  { name: 'Mike Hughes', role: 'Driver', color: 'bg-blue-100 text-blue-700' },
                  { name: 'Priya Patel', role: 'Porter', color: 'bg-green-100 text-green-700' },
                  { name: 'Daniel Kim', role: 'Sales Rep', color: 'bg-orange-100 text-orange-700' },
                ].map(u => (
                  <div key={u.name} className="py-2.5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-600">
                        {u.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div className="text-sm text-gray-800">{u.name}</div>
                    </div>
                    <div className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.color}`}>{u.role}</div>
                  </div>
                ))}
              </div>
            </div>
          </MockFrame>
        </StepCard>

        {/* Step 5 — First Job */}
        <StepCard
          number={5}
          title="Create Your First Job"
          subtitle="Start managing customer moves"
          testid="step-5"
        >
          <p>Click <strong>+ New Job</strong> from the dashboard to create a job:</p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>Enter customer contact information</li>
            <li>Add pickup and delivery addresses (Google Maps autocomplete)</li>
            <li>Set the move date and time</li>
            <li>Add inventory items (manually or using AI photo scanning)</li>
            <li>Generate an estimate and send to customer</li>
            <li>Assign team members to the job</li>
          </ol>

          <MockFrame url="https://admin.movemate.tech/jobs/new">
            <div className="p-6">
              <h4 className="font-semibold text-gray-900 mb-4">New Job — JOB-2026-042</h4>
              <div className="grid sm:grid-cols-2 gap-3">
                <FakeField label="Customer Name" value="Emma Thompson" />
                <FakeField label="Customer Phone" value="+44 7891 234567" />
                <FakeField label="From Address" value="42 Camden Lock, London NW1" />
                <FakeField label="To Address" value="7 Brighton Marina, Brighton BN2" />
                <FakeField label="Move Date" value="14 March 2026, 09:00" />
                <FakeField label="Estimated Hours" value="6 hours" />
              </div>
              <div className="mt-4 flex gap-2">
                <div className="bg-primary text-white px-4 py-1.5 rounded text-xs font-semibold">Save &amp; Send Estimate</div>
                <div className="border border-gray-300 text-gray-700 px-4 py-1.5 rounded text-xs font-semibold">Save Draft</div>
              </div>
            </div>
          </MockFrame>
        </StepCard>

        {/* Step 6 — Google Calendar */}
        <StepCard
          number={6}
          title="Connect Google Calendar (Optional)"
          subtitle="Sync jobs to your calendar"
          testid="step-6"
        >
          <p>Keep your schedule in sync by connecting Google Calendar:</p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>Go to <strong>Settings → Company Settings</strong></li>
            <li>Find the Google Calendar section</li>
            <li>Click <strong>Connect Google Calendar</strong></li>
            <li>Sign in with your Google account and grant permission</li>
            <li>Jobs will automatically sync to your calendar</li>
          </ol>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4 flex items-start gap-3">
            <Calendar className="text-blue-600 flex-shrink-0 mt-1" size={20} />
            <div>
              <p className="text-blue-800 font-medium">What gets synced:</p>
              <ul className="text-blue-700 text-sm mt-1 space-y-1">
                <li>• Job date, time, and duration</li>
                <li>• Customer name and addresses</li>
                <li>• Assigned team members</li>
              </ul>
              <Link to="/docs/google-calendar" className="text-blue-700 underline text-sm mt-2 inline-block font-medium">
                Read the full Google Calendar integration guide →
              </Link>
            </div>
          </div>
        </StepCard>

        {/* Troubleshooting */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-8" data-testid="troubleshooting-section">
          <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <HelpCircle className="text-primary" size={24} /> Common Setup Issues
          </h2>
          <p className="text-gray-600 mb-6">Hit a snag? Here's how to unstick the most common ones.</p>
          <div className="space-y-5">
            <Troubleshoot q="I didn't get the verification email">
              Check your spam folder. Most providers deliver within 2 minutes. If it still hasn't arrived, go to
              the login page and click <em>Resend verification email</em>. Gmail and Outlook are reliable;
              some corporate firewalls block transactional mail — try a personal inbox temporarily.
            </Troubleshoot>
            <Troubleshoot q="My logo looks blurry on estimates">
              Upload a PNG or SVG at least 512×512 px. We auto-scale down but can't scale up —
              low-resolution JPEGs get pixelated on PDF exports.
            </Troubleshoot>
            <Troubleshoot q="Team member invitation link expired">
              Invitations expire after 7 days. Go to <strong>Team → User Management</strong>, find the
              pending user, and click <em>Resend Invite</em>. The new link is good for another 7 days.
            </Troubleshoot>
            <Troubleshoot q="Google Calendar not syncing new jobs">
              Open the job and confirm it has a scheduled date and time (not just a date). Calendar sync
              only runs for jobs with a fixed start time. Still stuck? See the&nbsp;
              <Link to="/docs/google-calendar" className="text-primary underline font-medium">full integration guide</Link>.
            </Troubleshoot>
            <Troubleshoot q="Address autocomplete isn't working">
              Address autocomplete uses the company-wide Google Maps API key. Ask your admin to add a
              valid key in <strong>Settings → Integrations → Google Maps</strong>.
            </Troubleshoot>
          </div>
        </div>

        {/* What's Next — only routes that actually exist + external feature pages */}
        <div className="bg-gradient-to-r from-primary/10 to-cyan-500/10 rounded-xl p-8 mb-8" data-testid="whats-next-section">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">What's Next?</h2>
          <p className="text-gray-600 mb-6">Explore the features most teams use on day one:</p>
          <div className="grid md:grid-cols-2 gap-4">
            <Link to="/docs/google-calendar" className="bg-white p-4 rounded-lg hover:shadow-md transition flex items-center gap-3 border border-transparent hover:border-primary/20" data-testid="next-google-cal">
              <Calendar className="text-blue-500 flex-shrink-0" size={24} />
              <div>
                <p className="font-medium text-gray-900">Google Calendar Integration</p>
                <p className="text-sm text-gray-500">Full walkthrough of calendar sync</p>
              </div>
            </Link>
            <a href="https://movemate.tech/pricing" className="bg-white p-4 rounded-lg hover:shadow-md transition flex items-center gap-3 border border-transparent hover:border-primary/20" data-testid="next-pricing">
              <Zap className="text-purple-500 flex-shrink-0" size={24} />
              <div>
                <p className="font-medium text-gray-900">Unlock Premium Features</p>
                <p className="text-sm text-gray-500">AI scanning, SMS, white-label &amp; more</p>
              </div>
            </a>
            <a href="https://movemate.tech/faq" className="bg-white p-4 rounded-lg hover:shadow-md transition flex items-center gap-3 border border-transparent hover:border-primary/20" data-testid="next-faq">
              <HelpCircle className="text-green-500 flex-shrink-0" size={24} />
              <div>
                <p className="font-medium text-gray-900">Frequently Asked Questions</p>
                <p className="text-sm text-gray-500">Pricing, features, mobile, security</p>
              </div>
            </a>
            <Link to="/contact" className="bg-white p-4 rounded-lg hover:shadow-md transition flex items-center gap-3 border border-transparent hover:border-primary/20" data-testid="next-contact">
              <MessageSquare className="text-pink-500 flex-shrink-0" size={24} />
              <div>
                <p className="font-medium text-gray-900">Book a Personal Onboarding</p>
                <p className="text-sm text-gray-500">Free 30-minute call with our team</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Bottom nav */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4" data-testid="bottom-nav">
          <Link to="/docs" className="text-primary hover:underline flex items-center gap-2 font-medium">
            <ArrowLeft size={20} />
            Back to Documentation
          </Link>
          <Link to="/docs/google-calendar">
            <Button className="flex items-center gap-2" data-testid="next-page-btn">
              Google Calendar Guide
              <ArrowRight size={20} />
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-white py-8 px-4 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-500 text-sm">
            Need help? <Link to="/contact" className="text-primary hover:underline font-medium">Contact Support</Link>
            {' '}or email <a href="mailto:info@movemate.tech" className="text-primary hover:underline">info@movemate.tech</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

/* ---------- Small local components ---------- */
const StepCard = ({ number, title, subtitle, children, testid }) => (
  <div className="bg-white rounded-xl shadow-sm p-8 mb-6" data-testid={testid}>
    <div className="flex items-center gap-4 mb-6">
      <div className="w-12 h-12 bg-gradient-to-br from-primary to-cyan-500 text-white rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
        {number}
      </div>
      <div>
        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
        <p className="text-gray-500 text-sm">{subtitle}</p>
      </div>
    </div>
    <div className="space-y-4 text-gray-600">{children}</div>
  </div>
);

const BulletCheck = ({ children }) => (
  <li className="flex items-start gap-2">
    <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={18} />
    <span>{children}</span>
  </li>
);

const BulletIcon = ({ icon: Icon, color, children }) => (
  <li className="flex items-start gap-2">
    <Icon className={`${color} mt-1 flex-shrink-0`} size={18} />
    <span>{children}</span>
  </li>
);

const TipBox = ({ tone = 'blue', children }) => {
  const tones = {
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    green: 'bg-green-50 border-green-200 text-green-800',
  };
  return (
    <div className={`border rounded-lg p-4 mt-4 text-sm ${tones[tone]}`}>
      <strong>Tip:</strong> {children}
    </div>
  );
};

const Troubleshoot = ({ q, children }) => (
  <div>
    <h4 className="font-semibold text-gray-900 mb-1.5">{q}</h4>
    <p className="text-gray-600 text-sm leading-relaxed">{children}</p>
  </div>
);

export default QuickStartGuidePage;
